import {
  BookA,
  Briefcase,
  CircleHelp,
  Compass,
  KeyRound,
  Route,
  ShieldCheck,
  SlidersHorizontal,
  UsersRound,
} from 'lucide-react'
import type { HelpSection } from './types'

/**
 * The Help centre: how to use Fruuit Engage, written for asset managers, ESG analysts,
 * the Fruuit team and administrators. It describes what the screens do today, in the words
 * the screens use. Nothing here is secret, so every signed-in user can read every section.
 * Keep it true: when a screen or a rule changes, change the matching text in the same commit.
 */
export const HELP_SECTIONS: readonly HelpSection[] = [
  // ---------------------------------------------------------------------------------------
  {
    id: 'welcome',
    title: 'Welcome to Fruuit Engage',
    summary: 'What the portal is, its three areas, and how to find your way around.',
    icon: Compass,
    audience: 'all',
    keywords: ['introduction', 'overview', 'start', 'areas', 'demo', 'fictional'],
    blocks: [
      {
        type: 'p',
        text: 'Fruuit Engage is the portal for Fruuit Consulting’s stewardship and norms-screening service. Fruuit’s research team screens companies against international norms (the UN Global Compact, the OECD Guidelines and the UN Guiding Principles), opens engagement cases with companies where a concern needs to change, and records the evidence as it arrives. Investors see the reviewed, released results for the companies in their portfolios and the engagements their firm follows: the finding, the objectives, the progress so far and what remains to be proven.',
      },
      { type: 'h', text: 'Three areas, one sign-in' },
      {
        type: 'table',
        head: ['Area', 'Who uses it', 'What it is for'],
        rows: [
          [
            'Investor',
            'People at investor firms',
            'Your portfolio and its coverage, company pages, the engagement cases your firm follows, meetings, your questions and requests, reports and exports.',
          ],
          [
            'Workspace',
            'The Fruuit team',
            'Research and assessments, engagement cases, the review and release queue, meetings and minutes, feed signals and the client inbox.',
          ],
          [
            'Admin',
            'Platform administrators',
            'Organisations, users and invitations, subscriptions and data entitlements, data feeds, alerts and the audit log.',
          ],
        ],
      },
      {
        type: 'p',
        text: 'You see only the areas your roles allow. If you hold more than one, the area switcher in the top bar (`Investor` · `Workspace` · `Admin`) moves between them. The left-hand navigation lists the pages of the area you are in; on a phone, the menu button at the top left opens it.',
      },
      {
        type: 'callout',
        kind: 'know',
        title: 'This deployment is a demonstration',
        text: 'Every company, investor firm, holding, event, meeting and data-feed record in it is fictional, as the green banner at the top of each page says. No investment instruction, email or calendar invitation is ever sent from the portal.',
      },
      { type: 'h', text: 'Using this Help centre' },
      {
        type: 'list',
        items: [
          'Start with the section for your role: [For investors](/help/investors), [For the Fruuit team](/help/fruuit-team) or [For administrators](/help/administrators). The [User types and roles](/help/roles) section explains who can see and do what.',
          'The [Workflows](/help/workflows) section walks through three complete journeys, step by step. The [Glossary](/help/glossary) and [FAQ](/help/faq) answer the most common questions.',
          'Use the filter box above the contents to find a heading. On a phone, use the section menu at the top of each page.',
          '`Print` on any section gives you a clean copy; the navigation is left out.',
          'Definitions of every finding, coverage label, stage and status the portal shows are also on the [Methodology](/app/methodology) page in the investor area.',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'signing-in',
    title: 'Signing in',
    summary: 'Microsoft, Google or an emailed code; invitations; two-factor sign-in; signing out.',
    icon: KeyRound,
    audience: 'all',
    keywords: [
      'login',
      'password',
      'code',
      'invitation',
      'invite',
      'two-factor',
      'authenticator',
      'recovery codes',
      'sign out',
      'session',
      'no access',
    ],
    blocks: [
      {
        type: 'p',
        text: 'There are no passwords in Fruuit Engage. You sign in with an account you already have, or with your work email and a code: one we send you by email, or, once you have set up an authenticator app, the code from that app.',
      },
      { type: 'h', text: 'Three ways to sign in' },
      {
        type: 'seedo',
        rows: [
          [
            '`Continue with Microsoft`',
            'Use your work or school Microsoft account. Personal Microsoft accounts are not accepted. The first time an organisation uses it, its IT administrator may need to approve Fruuit Engage once; until then, use one of the other two options.',
          ],
          [
            '`Continue with Google`',
            'Use a Google Workspace or Google account whose email address matches your invitation.',
          ],
          [
            '`Sign in` with your work email',
            'Enter your work email and choose `Sign in`. If you have not set up an authenticator app, we email you a 6-digit code: it is valid for 15 minutes, can be used once, and you can ask for a new one after 60 seconds. If you have set up an authenticator app, no email is sent; enter the 6-digit code the app shows, or one of your recovery codes. Five wrong attempts pause sign-in for that address for 30 minutes.',
          ],
        ],
      },
      { type: 'h', text: 'Access is by invitation' },
      {
        type: 'p',
        text: 'Someone must invite you before you can sign in: your Fruuit Consulting contact, or an investor admin at your own firm. The invitation goes to your work email, is valid for 7 days and can be used once. Sign in with the **same email address** the invitation was sent to, using any of the three options, and the invitation is accepted automatically. If it has expired, ask for it to be re-sent.',
      },
      {
        type: 'seedo',
        caption: 'If something goes wrong',
        rows: [
          [
            '“This account doesn’t have access”',
            'The address you signed in with is not invited. The page deliberately looks the same whether or not an account exists. Check you used the invited address, then ask your Fruuit contact or your firm’s investor admin for an invitation.',
          ],
          [
            '“Your organisation is suspended”',
            'Access for your whole firm is paused. Your Fruuit Consulting contact can tell you more.',
          ],
          [
            'No code arrives',
            'Check spam, wait 60 seconds and ask again. If the address is not invited, no code is sent, by design.',
          ],
          [
            'Microsoft sign-in is refused',
            'The account may be a personal one, or your organisation has not yet approved the app. Use Google or an emailed code meanwhile.',
          ],
        ],
      },
      { type: 'h', text: 'Two-factor sign-in' },
      {
        type: 'p',
        text: 'You can add an authenticator app (Microsoft Authenticator, Google Authenticator, 1Password and similar). It is optional today, and recommended for anyone who approves, publishes or administers. Once it is set up, signing in by email asks for the code from the app instead of emailing one, and privileged actions use the same app for their extra check.',
      },
      {
        type: 'steps',
        items: [
          'Open the user menu (your name, top right) → `Account & security`.',
          'Under `Two-factor authentication`, start the set-up, scan the QR code with your app and enter the 6-digit code it shows.',
          'Save the **10 recovery codes** shown once, somewhere safe, then choose `I’ve saved them`. Each code can be used once if you lose your phone.',
          'To turn it off later, open the same panel and confirm with a current code.',
        ],
      },
      {
        type: 'callout',
        kind: 'tip',
        title: 'Lost your phone?',
        text: 'Sign in with your work email and enter one of your recovery codes where the authenticator code is asked for. Each works once. If you have none left, ask a platform administrator: under Admin → Users they can `Reset two-factor authentication` for your account, after which an emailed code signs you in again.',
      },
      { type: 'h', text: 'Sessions and signing out' },
      {
        type: 'list',
        items: [
          'You stay signed in while you keep using the portal. After 12 hours without activity, or 30 days in all, you are asked to sign in again.',
          '`Sign out` in the user menu ends the session on this device.',
          '`Account & security` → `Where you’re signed in` lists every device and browser you are signed in on. `Sign out everywhere` ends all of them at once; use it if you have signed in on a shared computer.',
          'A platform administrator can also sign you out everywhere, and a deactivated account is signed out on its next click.',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'roles',
    title: 'User types and roles',
    summary: 'Who the six roles are, what each can see and do, and what they can’t.',
    icon: UsersRound,
    audience: 'all',
    keywords: [
      'investor',
      'investor admin',
      'engagement manager',
      'reviewer',
      'platform admin',
      'platform operator',
      'permissions',
      'independence',
      'view as',
    ],
    blocks: [
      {
        type: 'p',
        text: 'A person’s roles decide which areas they see and what they can do there. Roles come with membership of an organisation (an investor firm or Fruuit Consulting), except the two platform roles, which stand on their own. One person can hold several roles: everyone admitted as a Fruuit platform admin is also an engagement manager and a reviewer, so they see all three areas.',
      },
      {
        type: 'table',
        head: ['Role', 'Who they are', 'What they see', 'What they can do', 'What they can’t do'],
        rows: [
          [
            'Investor',
            'An analyst, portfolio manager or committee member at an investor firm.',
            'The Investor area for their firm: portfolio coverage and company pages for the companies the firm holds or follows; released engagement cases the firm subscribes to; published meetings and minutes; the firm’s reports and export history.',
            'Ask the manager a question, request to join a meeting, record a client action, save views, set a default portfolio, export CSV and the committee report.',
            'See drafts or anything marked internal; see another firm’s data or cases the firm doesn’t subscribe to; read colleagues’ questions and requests; invite colleagues.',
          ],
          [
            'Investor admin',
            'The person who runs the relationship for the firm, often the head of stewardship or active ownership.',
            'Everything an investor sees, plus every colleague’s questions, requests and client actions in the firm.',
            'Everything an investor can do, plus invite colleagues to the firm and import or edit the firm’s holdings.',
            'Change what the firm subscribes to or which data feeds it is entitled to (Fruuit’s administrators do that); see other firms.',
          ],
          [
            'Engagement manager',
            'A Fruuit consultant who researches companies and runs engagement cases.',
            'The Workspace: every case, assessment, meeting and minutes, the review queue, feed signals, coverage for every client and the cross-client inbox.',
            'Create and edit drafts (assessments, objectives, evidence reviews, correspondence), submit them for review, run the meetings and minutes of their own cases, reply to investor questions, respond to participation requests on their cases, triage signals, resolve mapping exceptions, import holdings for a client.',
            'Approve, publish, release or retract anything; act on participation requests for another manager’s case (they can reassign them); administer users.',
          ],
          [
            'Reviewer',
            'A senior Fruuit colleague who checks work independently before it reaches investors.',
            'The Workspace, like a manager.',
            'Approve, publish, return or retract records; release engagement and objective summaries; approve and publish meeting minutes. Reviewers may also draft.',
            'Review anything they helped write (the portal refuses it); review a case’s evidence reviews, correspondence or minutes unless they are that case’s assigned reviewer.',
          ],
          [
            'Platform admin',
            'A Fruuit leader who looks after access and configuration.',
            'The Admin area: organisations, users, invitations, Fruuit staff roles, subscriptions and entitlements, data feeds, alerts and the platform audit log.',
            'Create organisations and suspend them; invite and deactivate users; change roles; reset two-factor and sign people out; grant and revoke subscriptions and data entitlements; acknowledge alerts.',
            '`View as` another user; re-run a data feed by hand; open the Dev docs.',
          ],
          [
            'Platform operator',
            'The person who runs the platform itself.',
            'Everything a platform admin sees, plus the technical Dev docs.',
            'Everything a platform admin can do, plus `View as` (see the portal as another user for 60 minutes, with a recorded reason), re-run data feeds and grant operator rights.',
            'Change their own two-factor settings or invitations while viewing as someone else.',
          ],
        ],
      },
      { type: 'h', text: 'Three rules worth knowing' },
      {
        type: 'list',
        items: [
          '**Independence.** Nothing reaches investors until a second person approves it. Anyone who created or edited a draft cannot approve, publish, return or retract it, whatever their role. For a case’s evidence reviews, correspondence and minutes, that second person must be the reviewer assigned to the case.',
          '**Released content only.** Investors see a record only once it is published or released, and only for engagements their firm subscribes to or companies the firm holds or follows. A page that is not available to you simply says so; it never reveals what exists.',
          '**Recorded, not sent.** Questions, participation requests and client actions stay inside the portal. Nobody is emailed and no calendar invitation is issued, to companies or to investors.',
        ],
      },
      {
        type: 'callout',
        kind: 'know',
        title: '“View as” is for administrators, not a role',
        text: 'A platform operator can view the portal as any active user for 60 minutes, for support or a demonstration. An amber banner shows whose view it is and who started it, the reason is recorded in the audit log, and every action taken in that time is marked as taken on that person’s behalf. See [For administrators](/help/administrators).',
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'investors',
    title: 'For investors',
    summary:
      'Your dashboard, portfolio and company pages, engagement cases, questions and requests, meetings, reports and saved views.',
    icon: Briefcase,
    audience: 'app',
    keywords: [
      'dashboard',
      'portfolio',
      'coverage',
      'company',
      'engagement',
      'case',
      'stage',
      'question',
      'participation',
      'meeting',
      'client action',
      'report',
      'committee',
      'export',
      'csv',
      'saved view',
      'methodology',
    ],
    blocks: [
      {
        type: 'p',
        text: 'The Investor area shows your firm’s portfolio through Fruuit’s research: which companies are covered and how fresh the review is, what Fruuit found, which engagements your firm follows and how they are progressing. Everything you see here has been reviewed and released by Fruuit; drafts and internal working notes never appear.',
      },
      { type: 'h', text: 'Dashboard' },
      {
        type: 'seedo',
        rows: [
          [
            'The four tiles: `Companies in coverage`, `Open engagements`, `Need attention`, `Released records`',
            'A quick read of your programme. `Need attention` counts stalled cases and holdings whose identifier could not be matched.',
          ],
          [
            '`Latest developments`',
            'The most recent published updates across your cases: new evidence reviews, assessments and minutes. `All published updates` opens the full list.',
          ],
          [
            '`Upcoming meetings`',
            'Published company meetings and investor briefings on the cases you follow. Open one to see its agenda and, afterwards, the reviewed minutes.',
          ],
          [
            '`Needs your attention`',
            'Your open questions and participation requests, and client actions still marked for discussion.',
          ],
          [
            '`Stalled cases and mapping exceptions`',
            'Cases where the company has stopped responding, and holdings that are not yet matched to a company.',
          ],
          [
            '`From dialogue to evidence`',
            'How many objectives sit at each stage of the ladder, from Baseline to Outcome evidenced.',
          ],
        ],
      },
      { type: 'h', text: 'Portfolio and coverage' },
      {
        type: 'p',
        text: '`Portfolio` lists every holding in the portfolio you choose, with the company it maps to, Fruuit’s latest published finding and a coverage label. Filter by finding, sector, theme or mapping state, or search by company, security or finding. `Make this my default portfolio` opens this portfolio first next time.',
      },
      {
        type: 'table',
        caption: 'Coverage labels',
        head: ['Label', 'What it means', 'What to do'],
        rows: [
          [
            'Within recorded scope',
            'Fruuit’s latest source check completed and the next review is not yet due.',
            'Nothing. The finding stands.',
          ],
          [
            'Review overdue',
            'The scheduled review date has passed. The last published finding still stands until it is reviewed.',
            'Treat the finding as older than usual. Ask the manager if the company matters to a decision.',
          ],
          [
            'Coverage gap: source check partial',
            'The latest source check did not cover every planned source, so it cannot support a clean finding.',
            'Read the finding with that limit in mind.',
          ],
          [
            'Coverage gap: source check failed',
            'The latest source check failed. Coverage is unknown, not clear, until it is run again.',
            'Do not read “no concern” into it. Ask the manager if you need a view now.',
          ],
          [
            'Source review not completed',
            'No completed source check is recorded for this company yet.',
            'The company is mapped but not yet reviewed. It appears as `Not yet reviewed`.',
          ],
        ],
      },
      {
        type: 'table',
        caption: 'Mapping states (how a holding was matched to a company)',
        head: ['State', 'What it means'],
        rows: [
          [
            'verified',
            'The holding’s identifier matches a company in Fruuit’s master by a typed identifier.',
          ],
          [
            'needs review',
            'A possible match exists or the identifier is a client code; a Fruuit manager confirms it.',
          ],
          [
            'unresolved',
            'The identifier is not in the master. The holding stays visible as a mapping exception until Fruuit resolves it.',
          ],
          [
            'out of scope',
            'Outside corporate coverage, for example a sovereign or municipal bond.',
          ],
        ],
      },
      {
        type: 'callout',
        kind: 'tip',
        text: 'Filtered the register just the way you need it? `Save this view` keeps the filters under a name of your choice; pick it again from `Saved views`. Views are private to you.',
      },
      { type: 'h', text: 'Company pages' },
      {
        type: 'p',
        text: 'Open a company from the portfolio, a case or a published update. The page has six sections: `Overview`, `Coverage`, `Engagement`, `Evidence`, `Client actions` and `History`.',
      },
      {
        type: 'seedo',
        rows: [
          [
            '`Fruuit assessment`',
            'The published finding (one of `No material concern identified`, `Further review required`, `Substantiated norms concern`, `Insufficient information`), with its confidence, severity, the norms involved, the rationale, `Evidence reviewed` (the effective date of the assessment) and `Next scheduled review`.',
          ],
          [
            '`Published assessment history`',
            'Earlier published versions, including corrections. A corrected version says what it changed.',
          ],
          [
            '`Evidence library` and `Incidents`',
            'The sources and incidents behind the finding. Only sources Fruuit may share with clients are listed; a line such as “2 further evidence references are internal to Fruuit” tells you how many are not.',
          ],
          [
            '`Engagement plan`',
            'If your firm follows a case on this company: its objectives, reviewed progress and next steps, with a link to the case.',
          ],
          [
            '`Your actions & decisions`',
            'Client actions your firm has recorded on this company. `Record client action` adds one.',
          ],
        ],
      },
      { type: 'h', text: 'Engagements and cases' },
      {
        type: 'p',
        text: '`Engagements` lists the cases your firm follows: a company, the issue, its status (`Active`, `Stalled` or `Closed`), the current stage and the next deadline. Filter by status, theme or stage, search, or save a view. Click the case title to open it.',
      },
      {
        type: 'seedo',
        caption: 'On a case page',
        rows: [
          [
            '`The engagement record`',
            'The summary Fruuit released: the change sought, the engagement manager, when the engagement started, `Fruuit coverage since` and the next deadline.',
          ],
          [
            '`Objectives & reviewed progress`',
            'Each objective with its baseline, the change requested, and a stage track. Every published evidence review states **what the evidence shows** and **what remains unproven**: the second line is deliberate, so you always know the limit of the claim.',
          ],
          [
            '`Company responsiveness`',
            'Worked out from the published correspondence: `Responsive`, `Slow` (a request has waited more than three weeks), `Unresponsive` (three or more unanswered requests, or more than 60 days) or `Not assessed`.',
          ],
          [
            '`Everything that happened, in order`',
            'The timeline of released correspondence and meetings: letters sent, responses received, evidence received, meetings held and their minutes.',
          ],
          [
            '`Your relationship team`, `Your participation`, `Your questions`',
            'Who runs the case at Fruuit, your firm’s participation requests on it and your question threads.',
          ],
        ],
      },
      { type: 'diagram', id: 'stage-ladder' },
      {
        type: 'p',
        text: 'An objective’s stage is the stage of its latest published evidence review; only published, independently reviewed evidence moves a stage. Stages measure what has been evidenced about the company, not investment performance. Case status answers a different question: a company can be `Active` in dialogue while the evidence is still at `S1`, or `Stalled` at `S2` because it stopped responding.',
      },
      { type: 'h', text: 'Asking the manager a question' },
      {
        type: 'steps',
        items: [
          'Open `Requests` (also in the top bar) or the case itself, and choose `Ask the manager`.',
          'Pick the engagement, give the question a subject and write it out. Send it.',
          'The thread shows as `Awaiting reply` until the engagement manager answers, then `Answered`. You can add a follow-up message in the same thread at any time, and every message stays on the record.',
        ],
      },
      {
        type: 'callout',
        kind: 'know',
        text: 'Nothing leaves Fruuit Engage. The manager is not emailed and neither are you; check `Requests` or the case for the reply. Your colleagues see your questions only if they are an investor admin at your firm.',
      },
      { type: 'h', text: 'Requesting to join a meeting' },
      {
        type: 'p',
        text: 'From `Requests` or a case, choose `Request participation`, then what you would like: `Discuss participation`, `Join a future company meeting` or `Receive the next evidence update`. Add a note for the engagement manager. The request then moves through these states:',
      },
      { type: 'diagram', id: 'participation-states' },
      {
        type: 'table',
        head: ['State', 'What it means'],
        rows: [
          ['Requested', 'Sent. The engagement manager has not yet responded.'],
          ['Acknowledged', 'The manager has seen it and will come back to you.'],
          [
            'Awaiting company consent',
            'Fruuit has asked the company whether an investor may attend. Company meetings need the company’s agreement.',
          ],
          [
            'Confirmed',
            'You have a seat, or you will receive the update. The manager’s note says what to expect.',
          ],
          [
            'Declined',
            'Not possible this time, with the reason: often commercial confidentiality, a cancelled or rescheduled meeting, or a closed case.',
          ],
          ['Withdrawn', 'You withdrew the request yourself while it was still open.'],
        ],
      },
      {
        type: 'list',
        items: [
          'Every response carries the manager’s note and the time; `Request history` on the request shows them all.',
          'You can `Withdraw` your own request while it is open. Requests about a meeting must be made before it starts.',
          'If a meeting is rescheduled, your request stays with it; if it is cancelled, the manager declines the request and says why.',
        ],
      },
      { type: 'h', text: 'Recording client actions' },
      {
        type: 'p',
        text: 'A client action is your firm’s own decision about a company, recorded next to the research so the committee can see it later. From a company page or a case choose `Record client action`, pick one of `Continue monitoring`, `Request deeper research`, `Discuss escalation` or `Consider a portfolio restriction`, and add a note. It starts as `Recorded for discussion`; mark it `Discussed` once your committee has. You can edit or delete the actions you recorded.',
      },
      {
        type: 'callout',
        kind: 'know',
        text: 'A client action never changes Fruuit’s finding and is never sent to anyone, including Fruuit’s managers as a task. It is a record for your firm, visible to Fruuit in the client inbox.',
      },
      { type: 'h', text: 'Calendar and meeting pages' },
      {
        type: 'list',
        items: [
          '`Calendar` shows published meetings on the cases you follow, by `Month` or as an `Agenda`. Filter by engagement or company.',
          'A meeting page shows its type (`Company dialogue`, `Investor briefing`, `Evidence review` or `Follow-up`), the date and time in the meeting’s own time zone (with the UTC time underneath when they differ), the agenda, the objectives it serves, the format or location and the participation deadline.',
          'After the meeting, `Reviewed meeting minutes` appear once Fruuit’s reviewer has published them, with the commitments recorded and the follow-up. Until then the section says no reviewed minutes have been released yet.',
          'A cancelled or rescheduled meeting says so at the top; a rescheduled one links to its new date.',
          'No calendar invitations are sent. Add the meeting to your own calendar if you need a reminder.',
        ],
      },
      { type: 'h', text: 'Reports and exports' },
      {
        type: 'seedo',
        rows: [
          [
            '`Reports` → the report list',
            'Choose `Committee report`, `Coverage register`, `Engagement cases` or `Published updates`; `Record exports` give one file per record type. Set the filters (portfolio, period, status, search) and `Preview` shows the first rows and the total.',
          ],
          [
            '`Export CSV`',
            'Downloads exactly what the preview shows, as a CSV file that opens in Excel. Exports are limited to 5,000 rows; narrow by portfolio, period or status if you hit the limit.',
          ],
          [
            '`Open committee report`',
            'A print-ready page: an executive overview, an engagement narrative per case your firm follows, then the consolidated portfolio. The period defaults to the last 90 days. Use your browser’s Print → “Save as PDF” to keep a copy.',
          ],
          [
            '`Export history`',
            'Every file your firm has exported, with who, when, the scope, the number of rows and the file’s fingerprint (hash), so a report can be traced back to the data it was built from.',
          ],
        ],
      },
      {
        type: 'callout',
        kind: 'tip',
        text: 'The committee report is long when your firm follows many cases. Use Ctrl-F (Cmd-F on a Mac) with the company name to jump to its narrative.',
      },
      { type: 'h', text: 'Saved views and preferences' },
      {
        type: 'list',
        items: [
          '`Save this view` on the Portfolio and Engagements pages keeps the current filters. `Saved views` lists yours; you can rename or delete them.',
          '`Make this my default portfolio` on the Portfolio page remembers which portfolio opens first; `Clear default portfolio` undoes it.',
          'Your display name and two-factor settings are under `Account & security` in the user menu.',
        ],
      },
      { type: 'h', text: 'Methodology' },
      {
        type: 'p',
        text: 'The [Methodology](/app/methodology) page defines every finding, coverage label, stage, case status, confidence level, severity, mapping state and priority the portal shows, and explains what the different dates mean (event date, publication date, retrieved and reviewed, evidence reviewed). It is the reference to cite in committee papers.',
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'fruuit-team',
    title: 'For the Fruuit team',
    summary:
      'The manager dashboard, the case page, recording evidence, review and release, meetings and minutes, the inbox, signals, coverage and the audit trail.',
    icon: ShieldCheck,
    audience: 'workspace',
    keywords: [
      'manager',
      'reviewer',
      'dashboard',
      'case',
      'assessment',
      'evidence review',
      'milestone',
      'correspondence',
      'submit',
      'approve',
      'publish',
      'release',
      'retract',
      'correction',
      'minutes',
      'inbox',
      'signals',
      'coverage',
      'holdings',
      'mapping',
      'audit',
    ],
    blocks: [
      {
        type: 'p',
        text: 'The Workspace is where research is done and checked. Everything in it is internal until it has passed independent review and been published or released; the tags `Internal – not visible to investors` and `Released to investors` tell you which is which on every record.',
      },
      { type: 'h', text: 'The manager dashboard' },
      {
        type: 'p',
        text: '`Dashboard` opens on `My cases`; switch to `All managers` to see the whole programme. The tiles count `Overdue actions`, `Meetings, next 14 days`, `Outstanding responses` and `Drafts awaiting review`.',
      },
      {
        type: 'seedo',
        rows: [
          [
            '`Overdue actions`',
            'Objectives whose next action is past its due date. Open the case and record what happened, or move the date with a reason.',
          ],
          [
            '`Upcoming meetings`',
            'Published meetings in the next 14 days. Check the preparation and the participation requests on each.',
          ],
          [
            '`Outstanding responses`',
            'Letters and emails you sent that have had no company reply. These drive the responsiveness label investors see.',
          ],
          ['`Deadlines`', 'Objective deadlines in the next 30 days.'],
          [
            '`Waiting for your review`',
            'Drafts submitted for review that you can act on as reviewer.',
          ],
          [
            '`Work queue` and `Open work by client`',
            'Open questions, participation requests and client actions across clients, and the same by client.',
          ],
        ],
      },
      { type: 'h', text: 'The case page' },
      {
        type: 'p',
        text: '`Engagements` lists every case with its working fields, release status and subscribers. The case page is where the work happens:',
      },
      {
        type: 'seedo',
        rows: [
          [
            '`The engagement record`',
            'Manager, reviewer, next deadline and `Fruuit coverage since`. `Edit engagement` changes the working fields (title, theme, status, next action, due date). Edits stay internal until a reviewer releases them.',
          ],
          [
            '`What investors see`',
            'The released copy of the summary, side by side with your working version, so you can see exactly what subscribers read.',
          ],
          [
            '`Objectives & progress`',
            '`Add objective` records the baseline and the change requested. `Record evidence review` records what the evidence shows about an objective: the stage reached, the sources, what changed and what is still outstanding. Each objective’s `Progress record` lists its evidence reviews, every one tagged internal or released.',
          ],
          [
            '`Meetings`',
            'The card in the right-hand column: the next meeting with its date, format and participation deadline, then the upcoming and held meetings. Times are shown in the meeting’s own time zone, with the UTC time underneath when they differ.',
          ],
          [
            '`Correspondence & meetings`',
            '`Add correspondence` records a `Written response`, a `Company meeting record`, `Evidence received` or a `Follow-up request`. Follow-up requests count as outbound; the other three count as responses, and together they produce the `Company responsiveness` label.',
          ],
          [
            '`Participation requests`, `Investor questions`, `What clients decided`',
            'Everything clients have asked or recorded on this case. Respond here or from the `Inbox`.',
          ],
        ],
      },
      {
        type: 'callout',
        kind: 'know',
        text: 'Clicking a company name opens the company dossier; clicking the case title opens the case. The case summary is always internal, because it shows working fields, and a case that has never been released carries a `Not released to investors` notice at the top.',
      },
      { type: 'h', text: 'Recording evidence and assessments' },
      {
        type: 'list',
        items: [
          '`Assessments` → `New assessment` opens the workbench: the company, the finding, priority, confidence, severity, how the company is involved, the norms concerned, the rationale, and the evidence sources and incidents the finding rests on.',
          'Each source has a class (`Official`, `Company`, `Media`, `Civil society`, `Internal record`) and a sharing setting: `Shareable with clients` or `Internal to Fruuit`. Investors only ever see shareable sources; the rest are counted, not listed.',
          'A `No material concern identified` finding cannot be published without a completed source check behind it; an empty or failed check can never support a clean screen.',
          'An evidence review at `S3 Implementation evidenced` or `S4 Outcome evidenced` must cite evidence and state its limitations.',
          'Signals from the data feeds, incidents and assessments stay distinct: a signal is a lead, an incident is an event with sources, an assessment is Fruuit’s judgment.',
        ],
      },
      { type: 'h', text: 'Review and release, step by step' },
      { type: 'diagram', id: 'review-flow' },
      {
        type: 'steps',
        items: [
          '**Draft.** Create or edit the record. Only you and anyone else who edits it (the contributors) can see it change; investors see nothing.',
          '**Submit for review.** `Submit for review` locks the content. A contributor must do this. Add a note for the reviewer if useful.',
          '**Independent approval.** A reviewer who did not contribute opens it from `Review & release` (filters: `In review`, `Approved, awaiting publication`, `Unreleased changes`, `My drafts`, `Returned to me`). They `Approve` with a reason, or `Return for changes`, which clears any approval and sends it back to the contributors with their note.',
          '**Publish.** The reviewer then chooses `Publish`, again with a reason. From that moment every subscribing firm sees it, and the published version can no longer be edited.',
        ],
      },
      {
        type: 'table',
        caption: 'After publication',
        head: ['Need', 'What to do', 'What investors see'],
        rows: [
          [
            'Fix or update a published record',
            'Choose `Correct version N` → `Create corrected draft`, give the reason, edit, and send it through review again. Only one correction can be open at a time.',
            'The published version stays visible until the correction is published; then the new version, marked as a correction, replaces it. The history keeps both.',
          ],
          [
            'Withdraw a published record',
            '`Retract`, with a reason (reviewer only).',
            'The version disappears from their view at once. The history and the audit trail keep it.',
          ],
          [
            'Update an engagement or objective summary',
            'Edit the working fields, then a reviewer who has not edited them since the last release chooses `Release` with a reason.',
            'The released copy: title, theme, status, next action and due date (for objectives: title, baseline, change requested, next action and due date). Everything else on the case stays internal.',
          ],
        ],
      },
      {
        type: 'callout',
        kind: 'know',
        title: 'Why the portal refused your approval',
        text: '“You contributed to this draft” means you created or edited it, so someone else must review it. For evidence reviews, correspondence and minutes, only the case’s assigned reviewer can act. If a save is refused because “this record changed”, someone else edited it first: refresh and apply your change to the latest version.',
      },
      { type: 'h', text: 'Meetings and minutes' },
      {
        type: 'steps',
        items: [
          '**Prepare.** From the `Calendar`, choose `Schedule meeting` and pick the case (the case’s `Meetings` card then lists it): title, type (`Company dialogue`, `Investor briefing`, `Evidence review`, `Follow-up`), start and end with time zone, format or location, agenda, company participants, the objectives it serves and a participation deadline. It is a `Team draft` until published, visible to the case team on the case and to all staff in the calendar.',
          '**Publish the agenda.** `Publish agenda` makes the meeting visible to subscribing investors in their calendar and on the case. The case itself must already be released. Only the case’s assigned manager can do this.',
          '**Change of plan.** `Reschedule meeting` (new start and end, with a reason) creates a new published meeting and marks the old one `Rescheduled`, keeping its objectives and the participation requests. `Cancel meeting` needs a reason; the meeting stays listed on the case as cancelled.',
          '**Minutes.** After the meeting, the assigned manager writes the minutes (what was discussed, commitments recorded, follow-up) and chooses `Submit for review`. The assigned reviewer chooses `Approve minutes` then `Publish reviewed minutes` (`Publish to investors`), or `Return for changes`. Only the reviewer who approved can publish.',
          '**Corrections.** `Correct published minutes` creates a new version with a note on what the correction changes; it goes through review again.',
        ],
      },
      {
        type: 'callout',
        kind: 'tip',
        text: 'The meeting page’s `Preparation` block gathers what to bring: the objectives, the test for this meeting, open investor questions and the last correspondence. `Commitments recorded` after the meeting lists the evidence reviews at `S2` or above dated around the meeting.',
      },
      { type: 'h', text: 'Answering investors: the inbox' },
      {
        type: 'p',
        text: '`Inbox` brings together `Questions`, `Participation` requests and `Client actions` from every client. Filter to `Open (needs a response)` to see what is waiting.',
      },
      {
        type: 'seedo',
        rows: [
          [
            'A question',
            'Any manager can reply; the thread shows the reply to the investor at once. Close the thread when it is resolved.',
          ],
          [
            'A participation request',
            'The case’s assigned manager chooses `Acknowledge`, `Company consent pending` (you have asked the company), `Confirm` or `Decline`, each with a note that the investor sees in their `Request history`. Any manager can `Reassign` a request to another manager. A response needs an open case, a meeting that has not started and a firm that still has access.',
          ],
          [
            'A client action',
            'Read it; it is the client’s own decision, recorded for their committee. It is not a task for Fruuit, but `Request deeper research` and `Discuss escalation` are worth a reply through the case.',
          ],
        ],
      },
      { type: 'h', text: 'Signals from the data feeds' },
      {
        type: 'list',
        items: [
          '`Signals` lists what the data feeds have delivered about covered companies: Fruuit’s AI-assisted screening engine (FactSet raw company and controversy data, cross-validated against Sustainalytics research; the engine runs outside the portal and sends its output here), News Monitor and Company Disclosures (both simulated in this deployment). Each signal shows with a 0–100 score, a severity, the delivered identifier and how the company was matched.',
          'A signal is a candidate for human assessment, nothing more. It is never published and never sets a finding.',
          'Choose `Dismiss signal` with a reason (duplicate, outside scope, irrelevant), or `Promote to a draft incident`, which creates a draft incident with the signal as an unverified source. The incident then goes through the normal review workflow.',
          'A signal in `Mapping review` could not be matched to a company by identifier; resolve the identifier first.',
        ],
      },
      { type: 'h', text: 'Coverage, holdings import and mapping exceptions' },
      {
        type: 'list',
        items: [
          '`Coverage` shows every covered company and holding, per client. Choose the client to see their register as they see it, with the same labels.',
          '`Import holdings CSV` loads a client’s positions (a manager or the client’s investor admin can do this). Each line is matched by identifier: ISIN, LEI or a client code.',
          'Lines that cannot be matched become **mapping exceptions**: `unresolved` (identifier not in the master) or `needs review` (a client code or a possible match). `Resolve mapping exception` offers `Link to a covered security`, `Outside coverage (for example a municipal bond)` or `Dismiss without changing the holding`. Each exception is `Open`, `Resolved` or `Dismissed`.',
        ],
      },
      { type: 'h', text: 'The audit trail' },
      {
        type: 'p',
        text: '`Audit` lists every event on the research record: who did what, to which record, when, with the previous state and the new one. Events can never be edited or deleted, and each carries a hash that links it to the one before, so a break in the chain would be detected. `Verify now` recomputes the chain and reports the result.',
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'administrators',
    title: 'For administrators',
    summary:
      'Organisations and users, “View as”, subscriptions and data entitlements, data feeds and alerts, the audit log.',
    icon: SlidersHorizontal,
    audience: 'admin',
    keywords: [
      'organisation',
      'users',
      'invite',
      'deactivate',
      'reset two-factor',
      'view as',
      'impersonation',
      'subscription',
      'entitlement',
      'revoke',
      'restrict',
      'feeds',
      'alerts',
      'audit',
    ],
    blocks: [
      {
        type: 'p',
        text: 'The Admin area controls who can sign in, what each investor firm can see, and how the data feeds are behaving. Every change here needs a reason where it affects access, and every change is written to the audit log.',
      },
      { type: 'h', text: 'Organisations and users' },
      {
        type: 'seedo',
        rows: [
          [
            '`Orgs`',
            'Create and edit organisations. `Suspend` signs everyone in the firm out at once and blocks sign-in until you resume it; it is the only lifecycle action in this deployment.',
          ],
          [
            '`Users` → `Invite a person`',
            'Work email, organisation and role (`Investor` or `Investor admin` for a firm). The invitation is valid for 7 days and can be used once; `Pending invitations` lets you resend (a new 7 days) or revoke it. Inviting someone who already has an account grants the role at once.',
          ],
          [
            'A user’s drawer',
            '`Account` (status), `Organisations` (roles; `Remove from org`), `Sign-in` (how they sign in). From here: `Deactivate` or `Reactivate`, `Reset two-factor authentication` (they set it up again at next sign-in) and `Sign out everywhere`.',
          ],
          [
            '`Staff`',
            '`Invite Fruuit staff` and set who is an engagement manager and who is a reviewer. Platform users are not invited: anyone with a verified Fruuit Consulting address becomes a platform admin when they first sign in, and also receives the manager and reviewer roles.',
          ],
        ],
      },
      {
        type: 'callout',
        kind: 'know',
        text: 'Deactivating is immediate: the person is signed out on their next click. Their history, questions and audit entries stay.',
      },
      { type: 'h', text: 'Viewing the portal as a user (“View as”)' },
      {
        type: 'p',
        text: 'Use it to see exactly what a client sees, to support someone, or to demonstrate the investor view. It is available to platform operators only, from `Users`.',
      },
      {
        type: 'steps',
        items: [
          'Find the person in `Users` and choose `View as`.',
          'Give a reason of at least 8 characters (one is prefilled). It goes to the audit log.',
          'You now see the portal as that person for **60 minutes**. An amber banner shows whose view it is, who started it and the minutes left.',
          'Choose `Exit impersonation` in the banner (or the user menu) to return to your own session. The session also ends by itself after 60 minutes.',
        ],
      },
      {
        type: 'list',
        items: [
          'Everything you do while viewing is recorded as done on that person’s behalf, by you.',
          'Some things are locked while viewing: the person’s two-factor settings and sign-in identities, invitations, starting another “View as”, signing out everywhere and ending sessions.',
          'Only active users with an active membership can be viewed, not other administrators.',
        ],
      },
      { type: 'h', text: 'Subscriptions and data entitlements' },
      {
        type: 'p',
        text: '`Subscriptions & entitlements` decides which firm sees which engagement case and which data feeds. A **subscription** makes a shared case visible to a firm (a case opened for one client is always visible to that client). A **dataset entitlement** lets a firm see feed-derived research for one dataset. Both are granted and revoked with a reason.',
      },
      {
        type: 'seedo',
        rows: [
          [
            '`Revoke subscription`',
            'The firm stops seeing the case, its objectives, evidence reviews, correspondence, meetings and minutes from their next click. The dialog tells you how many of each disappear. The history of the grant is kept.',
          ],
          [
            '`Restrict a user`',
            'Hides one case from one person while their firm stays subscribed, for example a conflict of interest.',
          ],
          [
            '`Grant dataset access` / `Revoke dataset access`',
            'One active grant per firm and dataset. Match it to what the data licence allows.',
          ],
        ],
      },
      { type: 'h', text: 'Data feeds and alerts' },
      {
        type: 'p',
        text: '`Data feeds` shows each provider, its licence status, its schedule and a timeline of runs. The Fruuit Screening Engine is Fruuit’s AI-assisted screening process (FactSet raw data, cross-validated against Sustainalytics; both licences held) and shows `Licensed`. In this deployment all three feeds deliver fictional demo records.',
      },
      {
        type: 'table',
        caption: 'Run statuses',
        head: ['Status', 'What it means', 'What to do'],
        rows: [
          ['Succeeded', 'The provider delivered and every record was processed.', 'Nothing.'],
          [
            'Partial',
            'The delivery hit the provider’s record cap; some records may be missing.',
            'The next run usually catches up. Note it if a company is under urgent review.',
          ],
          [
            'No new records',
            'The run completed with nothing new. This is not evidence of no concern.',
            'Nothing, unless it keeps happening.',
          ],
          [
            'Late',
            'The delivery arrived after the expected time, or has not arrived yet.',
            'Wait for the next run; a long gap is worth a word with the operator.',
          ],
          [
            'Failed',
            'The run did not complete (a time-out, an error from the provider). It is retried automatically, up to three attempts.',
            'If the retries fail too, an alert is raised and emailed to the operations address.',
          ],
          ['Running', 'In progress now.', 'Nothing.'],
        ],
      },
      {
        type: 'list',
        items: [
          '`Run now` re-runs a provider by hand for a recent slot. It is limited to platform operators and is recorded.',
          '`Alerts` lists what needs attention: failed, partial and late runs, high-severity signals on companies a client holds, and job or audit-check failures, each `Information`, `Warning` or `Critical`. `Acknowledge` an alert once it is handled; the same alert is not raised again while it is open.',
        ],
      },
      { type: 'h', text: 'The audit log' },
      {
        type: 'p',
        text: '`Audit` in the Admin area is the platform log: sign-ins and refusals, every invitation, role change, deactivation, subscription, entitlement and “View as”. The badge at the top says `Chain verified` with the number of events, or `Chain broken at #…` if anything has been tampered with. Research events are on the Workspace audit page instead.',
      },
      { type: 'h', text: 'Preferences' },
      {
        type: 'p',
        text: 'Your own display name, two-factor settings and sessions are under `Account & security` in the user menu, like everyone else’s. There are no platform-wide settings to change from the browser; the deployment’s configuration is the operator’s job and is described in the Dev docs.',
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'workflows',
    title: 'Workflows, end to end',
    summary:
      'Three complete journeys: from a screening signal to a released finding, running a case to a commitment, and following a case through to the committee.',
    icon: Route,
    audience: 'all',
    keywords: ['walkthrough', 'journey', 'signal', 'finding', 'commitment', 'committee', 'example'],
    blocks: [
      {
        type: 'p',
        text: 'These walkthroughs follow the kind of case the demonstration uses: a mining company whose tailings facility leaked, a sanction, a board commitment to an independent panel, and an investor who follows the case and reports to her committee. Names are left out; the steps are the real ones.',
      },
      { type: 'h', text: 'From a screening signal to a released finding' },
      {
        type: 'steps',
        items: [
          'A data feed delivers a record about a seepage at the company’s site. It appears in `Signals` as `New`, scored by severity and matched to the company by its identifier.',
          'An engagement manager reads it and chooses `Promote to a draft incident`. A draft incident is created with the signal as an unverified source. (Routine news would be dismissed with a reason instead.)',
          'The manager collects the official record of the proceeding, the company’s response and media coverage as sources, marks which may be shared with clients, and completes the incident. A source check is run.',
          'In `Assessments` → `New assessment` the manager writes the finding (`Further review required` at this point), the confidence, severity, involvement and norms, and links the incident and sources. `Submit for review`.',
          'The reviewer opens it from `Review & release`, reads the evidence and chooses `Approve`, then `Publish`, each with a reason.',
          'Every firm that holds the company sees the published assessment on the company page and in `Latest developments`. The coverage label becomes `Within recorded scope`. Later, when the sanction names the company’s subsidiary rather than the parent, the manager corrects the assessment (`Correct version 2` → `Create corrected draft`) and the correction goes through the same review.',
        ],
      },
      { type: 'h', text: 'Running an engagement case to a commitment' },
      {
        type: 'steps',
        items: [
          'The manager opens a case on the company (`Engagements` → new engagement), names the issue and theme, and chooses the reviewer. `Add objective` records the baseline and the change requested: an independent review of the facility and a remedy for the affected community.',
          'The reviewer releases the case and its objective (`Release`, with a reason). Subscribing firms now see `The engagement record` and the objective at `S0 Baseline`.',
          'The manager writes to the company and records it as a `Follow-up request`. The company’s written reply is recorded as a `Written response`; `Company responsiveness` shows `Responsive`. Once dialogue is substantive, an evidence review at `S1 Dialogue established` is drafted, submitted, approved and published.',
          'A meeting with the company secretary is prepared on the case and its agenda published. An investor asks to join; the manager marks it `Company consent pending`, then `Confirm` when the company agrees.',
          'At the meeting the board commits to an independent panel. The manager writes the minutes, `Submit for review`; the reviewer chooses `Approve minutes` and `Publish reviewed minutes`.',
          'The manager chooses `Record evidence review` at `S2 Commitment recorded`, citing the minutes and the company’s letter, with what remains unproven (the panel is not yet appointed). After review and publication, the objective’s stage moves to `S2` for every subscribing firm. Months later, when the panel is appointed, a second `S2` review records it; a draft `S3` review waits for the first implementation evidence and stays `Internal – not visible to investors` until then.',
        ],
      },
      { type: 'h', text: 'An investor follows a case and reports to committee' },
      {
        type: 'steps',
        items: [
          'The head of active ownership at an investor firm signs in and opens `Engagements`. Her firm holds the company, so Fruuit has subscribed the firm to the case; she clicks the case title.',
          'On the case she reads the objective, the stage track and the latest evidence review: what the evidence shows (a board commitment, minuted) and what remains unproven (no panel yet, no remedy yet). `Company responsiveness` says `Responsive`.',
          'She has a question about groundwater monitoring: `Ask the manager`. The reply arrives in the thread a day later; nothing is emailed, so she checks `Requests`.',
          'Her analyst asks to join the next company meeting: `Request participation` → `Join a future company meeting`. The request moves to `Awaiting company consent` and then `Confirmed`; the manager’s note tells him the format.',
          'After the committee discusses the case she records a client action, `Discuss escalation`, with a note, and later marks it `Discussed`.',
          'For the quarterly committee she opens `Reports` → `Committee report` → `Open committee report`, prints it to PDF, and exports the `Engagement cases` CSV for the appendix. Both appear in `Export history` with their file fingerprint.',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'glossary',
    title: 'Glossary',
    summary: 'The words the portal uses, in plain English.',
    icon: BookA,
    audience: 'all',
    keywords: ['definitions', 'terms', 'vocabulary', 'meaning'],
    blocks: [
      {
        type: 'terms',
        items: [
          {
            term: 'Engagement (case)',
            text: 'Fruuit’s structured dialogue with a company about one issue, with objectives, evidence reviews, correspondence and meetings. “Case” and “engagement” mean the same thing. A case is `Active`, `Stalled` or `Closed`.',
          },
          {
            term: 'Objective',
            text: 'One change Fruuit seeks from the company within a case, with a baseline (where things stand) and the change requested. Its stage is the stage of its latest published evidence review.',
          },
          {
            term: 'Assessment',
            text: 'Fruuit’s versioned finding about a company against the international norms: `No material concern identified`, `Further review required`, `Substantiated norms concern` or `Insufficient information`, with confidence, severity and the evidence it rests on. Reviewed and published before investors see it.',
          },
          {
            term: 'Evidence review (milestone)',
            text: 'A dated record of what the evidence shows about an objective, the stage it supports and what remains unproven. Only a published evidence review moves an objective’s stage.',
          },
          {
            term: 'Stage',
            text: 'The five-step ladder for an objective: `S0 Baseline`, `S1 Dialogue established`, `S2 Commitment recorded`, `S3 Implementation evidenced`, `S4 Outcome evidenced`. It measures what has been evidenced, not investment performance.',
          },
          {
            term: 'Correspondence',
            text: 'The letters, emails, calls and meeting records exchanged with a company on a case: a `Follow-up request` from Fruuit, or a `Written response`, `Company meeting record` or `Evidence received` from the company.',
          },
          {
            term: 'Company responsiveness',
            text: 'A label worked out from published correspondence: `Responsive`, `Slow`, `Unresponsive` or `Not assessed`.',
          },
          {
            term: 'Draft, in review, approved, published',
            text: 'The states a record passes through. Investors see only `Published` records. `Returned for changes` sends a draft back to its authors; `Retracted` withdraws a published version.',
          },
          {
            term: 'Release / publish',
            text: 'Making reviewed content visible to subscribing investors. Records (assessments, evidence reviews, correspondence, minutes) are **published**; the summaries of an engagement and its objectives are **released**. Both need a reviewer who did not write the content.',
          },
          {
            term: 'Correction',
            text: 'A new version of a published record, created with a reason and reviewed again. The earlier version stays visible until the correction is published, and the history keeps both.',
          },
          {
            term: 'Independent review',
            text: 'The rule that a second person, who did not contribute to a draft, must approve and publish it. For a case’s evidence reviews, correspondence and minutes, that person is the reviewer assigned to the case.',
          },
          {
            term: 'Subscription',
            text: 'An investor firm’s right to see a shared engagement case. Granted and revoked by Fruuit’s administrators with a reason. A case opened for one client is visible to that client only.',
          },
          {
            term: 'Authorized company',
            text: 'A company an investor firm may see Fruuit’s research on: one the firm holds (with a verified mapping), one on the firm’s coverage list, or the company of a case the firm subscribes to.',
          },
          {
            term: 'Coverage label',
            text: 'How current Fruuit’s review of a company is: `Within recorded scope`, `Review overdue`, `Coverage gap: source check partial`, `Coverage gap: source check failed` or `Source review not completed`.',
          },
          {
            term: 'Mapping / mapping exception',
            text: 'How a holding was matched to a company by identifier (`verified`, `needs review`, `unresolved`, `out of scope`). A holding that could not be matched is a mapping exception until Fruuit resolves it.',
          },
          {
            term: 'Participation request',
            text: 'An investor’s request to `Discuss participation`, `Join a future company meeting` or `Receive the next evidence update`. It moves from `Requested` through `Acknowledged` and `Awaiting company consent` to `Confirmed`, or ends `Declined` or `Withdrawn`.',
          },
          {
            term: 'Question',
            text: 'A threaded question from an investor to the engagement manager about a case: `Awaiting reply`, `Answered` or `Closed`.',
          },
          {
            term: 'Client action',
            text: 'An investor firm’s own recorded decision about a company: `Continue monitoring`, `Request deeper research`, `Discuss escalation` or `Consider a portfolio restriction`. `Recorded for discussion` until the firm marks it `Discussed`. Never sent to anyone and never changes a finding.',
          },
          {
            term: 'Committee report',
            text: 'The print-ready report for an investor firm: an executive overview, a narrative per case the firm follows and the consolidated portfolio, watermarked and logged.',
          },
          {
            term: 'Export history',
            text: 'The log of every file exported for a firm, with who, when, the scope, the row count and the file’s fingerprint (hash).',
          },
          {
            term: 'Data feed / signal',
            text: 'A data feed is a source Fruuit monitors: the Fruuit Screening Engine, News Monitor and Company Disclosures (all delivering fictional records in this deployment). A signal is one delivered record about a company: a candidate for human assessment, never published on its own.',
          },
          {
            term: 'Screening engine',
            text: 'Fruuit’s AI-assisted screening process, which runs outside the portal: raw company and controversy data from FactSet, cross-validated against Sustainalytics research. The portal receives its output as signals that a Fruuit analyst assesses before anything is released.',
          },
          {
            term: 'Dataset entitlement',
            text: 'An investor firm’s right to see feed-derived research for one dataset, granted to match the data licence.',
          },
          {
            term: 'Audit trail',
            text: 'The permanent, hash-chained record of who did what and when. Events cannot be edited or deleted, and the chain can be verified at any time.',
          },
          {
            term: 'Canonical issuer',
            text: 'The single master record for a company (its legal entities, identifiers such as LEI and ISIN, sector and country) that every holding, source and case is matched to. Fruuit maintains it; clients never need to.',
          },
          {
            term: '“View as”',
            text: 'A platform operator seeing the portal as another user for 60 minutes, with a recorded reason and an amber banner.',
          },
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------------------
  {
    id: 'faq',
    title: 'FAQ',
    summary: 'Short answers to the questions people ask most.',
    icon: CircleHelp,
    audience: 'all',
    keywords: ['questions', 'answers', 'why', 'how', 'excel', 'email', 'approved', 'internal'],
    blocks: [
      {
        type: 'faq',
        items: [
          {
            q: 'Why can’t I see a case my colleague mentioned?',
            a: 'Three usual reasons. The case is not yet released by Fruuit’s reviewer; your firm does not subscribe to it (Fruuit’s administrators grant subscriptions); or it has been restricted for you specifically. A case that is not available simply says “This engagement is not available.” Ask your firm’s investor admin or your Fruuit contact.',
          },
          {
            q: 'Why is a record marked “Internal – not visible to investors”?',
            a: 'It is a draft, or it is in review, or it is a working copy that has not been released yet. Investors see a record only once it has been approved and published (or released) by a reviewer who did not write it.',
          },
          {
            q: 'Who approved this finding?',
            a: 'Fruuit staff see the full history (`History` on the record: who submitted, approved and published, when and why). Investors see the published version and its effective date; the names of Fruuit’s authors and reviewers appear as display names on the case page under `Your relationship team`. Every step is also in Fruuit’s audit trail.',
          },
          {
            q: 'Where do the signals come from?',
            a: 'From Fruuit’s AI-assisted screening engine, which runs outside the portal on raw company and controversy data from FactSet, cross-validated against Sustainalytics research, and from the News Monitor and Company Disclosures feeds. The portal receives the engine’s output as signals. A Fruuit analyst assesses every signal; nothing from a feed is published automatically, and no FactSet or Sustainalytics data is redistributed to investors.',
          },
          {
            q: 'Can I get an Excel export?',
            a: 'Exports are CSV files, which open directly in Excel, and the committee report is a print-ready page you can save as PDF. Native Excel workbooks and branded PDFs are not part of this version.',
          },
          {
            q: 'Does the portal email companies or investors?',
            a: 'No. The only emails the portal sends are sign-in codes, invitations and operational alerts to Fruuit. Questions, replies, participation responses and meetings live in the portal only, and no calendar invitations are issued.',
          },
          {
            q: 'How do I know whether a finding is still current?',
            a: 'Look at the coverage label next to it. `Within recorded scope` means the review schedule is being kept; `Review overdue` and the two `Coverage gap` labels tell you the finding should be read with care. `Evidence reviewed` on the company page is the effective date of the latest published assessment.',
          },
          {
            q: 'What does “what remains unproven” mean?',
            a: 'Every evidence review states both what the evidence shows and what it does not yet show. A commitment recorded at `S2` is a promise; implementation at `S3` and the outcome at `S4` need independent evidence of their own. The line is there so nobody reads more into a stage than the evidence supports.',
          },
          {
            q: 'I asked a question. Why haven’t I heard back?',
            a: 'Replies appear in the thread under `Requests` or on the case, not by email. The engagement manager sees your question in their inbox and dashboard; if it is urgent, your Fruuit contact can be reached in the usual way.',
          },
          {
            q: 'My request to join a meeting says “Awaiting company consent”. What now?',
            a: 'Fruuit has asked the company whether an investor may attend. You will see `Confirmed` with a note on the format, or `Declined` with the reason. You can withdraw the request at any time before then.',
          },
          {
            q: 'Why can’t I approve this record?',
            a: 'Because you contributed to it, or because you are not the reviewer assigned to the case (for evidence reviews, correspondence and minutes). Independence is enforced by the portal, not by convention. Ask the case’s reviewer or another reviewer.',
          },
          {
            q: 'The save was refused: “This record changed.”',
            a: 'Someone else saved a change to the same record first. Refresh, read their change and apply yours to the latest version. Nothing of yours was written over theirs.',
          },
          {
            q: 'A holding on my portfolio shows “unresolved”. Is it covered?',
            a: 'Not yet. Its identifier could not be matched to a company in Fruuit’s master, so no finding can be attached. It is listed as a mapping exception for Fruuit to resolve; once linked, the finding and coverage label appear.',
          },
          {
            q: 'Can I see what another person at my firm has asked or recorded?',
            a: 'Only if you are an investor admin for the firm. Investors see their own questions, requests and client actions; the portfolio, coverage and released research are shared across the firm.',
          },
          {
            q: 'What happens when a subscription is revoked?',
            a: 'The firm stops seeing the case and everything on it from their next click: objectives, evidence reviews, correspondence, meetings and minutes. Nothing is deleted at Fruuit, and the grant history is kept.',
          },
          {
            q: 'Is my data real?',
            a: 'In this deployment, no: every company, firm, holding, event and feed record is fictional, and the pages say so. The people who sign in (the Fruuit team and the platform operator) are real.',
          },
        ],
      },
    ],
  },
]

/** Which section opens first for each area. */
export const AREA_SECTION = {
  app: 'investors',
  workspace: 'fruuit-team',
  admin: 'administrators',
} as const

export const HELP_SECTION_IDS = HELP_SECTIONS.map((section) => section.id)

export function findHelpSection(id: string | undefined): HelpSection | undefined {
  return HELP_SECTIONS.find((section) => section.id === id)
}
